/**
 * RS-11b — the firm's RFIs & Submittals page in the Site Office vocabulary.
 *
 * Three pure pieces, props only, no fetching:
 *   TrailMast   the dark masthead: project code, the page name, the two doors
 *   TrailTabs   Tracker · Questions n · Submittals n
 *   TrackerFace four tiles and ONE list of what is open, questions and
 *               submittals together, each row with its Open button
 *
 * The page keeps every handler it has; these only draw.
 */
import React from 'react';
import '@/styles/site-office.css';
import '@/styles/rfi-trail.css';

/* Register line states, grouped by whose move it is. Mirrors
   services/submittals + routes/submittal_register — a state missing here is
   treated as "waiting on them". */
export const THEIR_MOVE = ['expected', 'sent_for_confirmation', 'revise', 'reissued'];
export const YOUR_MOVE = ['received', 'under_review', 'confirmed', 'returned'];
export const CLOSED = ['approved', 'approved_as_noted', 'rejected', 'released', 'accepted', 'accepted_by_both', 'spec_stands'];

const fmtDay = (iso) => (iso
  ? new Date(String(iso).length === 10 ? `${iso}T12:00:00` : iso)
    .toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  : '');

const isLate = (due) => Boolean(due) && new Date(`${String(due).slice(0, 10)}T23:59:59`) < new Date();

export function TrailMast({ projectName, questionCount = 0, submittalCount = 0, onNewQuestion, onNewSubmittal, onBack }) {
  return (
    <div className="ktr-mast" data-testid="trail-mast">
      <div>
        <button type="button" className="ktr-mast__code" onClick={onBack} data-testid="trail-back">
          ← {projectName || 'Project'} · paper trail
        </button>
        <div className="ktr-mast__name">RFIs &amp; Submittals</div>
        <div className="ktr-mast__sub" data-testid="trail-counts">
          {questionCount} {questionCount === 1 ? 'question' : 'questions'} · {submittalCount} {submittalCount === 1 ? 'submittal' : 'submittals'}
        </div>
      </div>
      <div className="ktr-mast__acts">
        <button type="button" className="kso-abtn" onClick={onNewQuestion} data-testid="trail-new-question">New question</button>
        <button type="button" className="kso-abtn brand" onClick={onNewSubmittal} data-testid="trail-new-submittal">New submittal</button>
      </div>
    </div>
  );
}

export function TrailTabs({ value, onChange, questionsOpen = 0, submittalsOpen = 0 }) {
  const tab = (key, label, n) => (
    <button type="button" aria-pressed={value === key} onClick={() => onChange(key)}
      data-testid={`trail-tab-${key}`}>
      {label}{n > 0 ? <i>{n}</i> : null}
    </button>
  );
  return (
    <span className="ktr-tabs" role="tablist" data-testid="trail-tabs">
      {tab('tracker', 'Tracker')}
      {tab('rfis', 'Questions', questionsOpen)}
      {tab('submittals', 'Submittals', submittalsOpen)}
    </span>
  );
}

/* One open row, whichever side it came from. */
export function openRows({ rfis = [], lines = [], ballOf }) {
  const rows = [];
  for (const r of rfis) {
    if (r.status === 'closed') continue;
    const ball = ballOf ? ballOf(r) : 'designer';
    rows.push({
      id: `q-${r.id}`, kind: 'question', tone: ball === 'designer' ? 'late' : 'wait',
      title: `${r.rfi_number} · ${r.subject}`,
      line: `${ball === 'designer' ? 'waiting on your answer' : `waiting on ${r.raised_by_name || r.submitted_by || 'them'}`}${r.submitted_at ? ` · asked ${fmtDay(r.submitted_at)}` : ''}`,
      due: r.due_date, late: isLate(r.due_date), yours: ball === 'designer', item: r,
    });
  }
  for (const l of lines) {
    if (CLOSED.includes(l.state)) continue;
    const yours = YOUR_MOVE.includes(l.state);
    rows.push({
      id: `s-${l.id}`, kind: 'submittal', tone: yours ? 'late' : 'wait',
      title: `${l.title}${l.kind_label ? ` · ${l.kind_label.toLowerCase()}` : ''}`,
      line: `${l.source?.kind === 'issued' ? 'issued to' : 'expected from'} ${l.party_name} · ${(l.state_label || l.state || '').toLowerCase()}`,
      due: l.due_date, late: isLate(l.due_date), yours, item: l,
    });
  }
  rows.sort((a, b) => (b.late - a.late) || (b.yours - a.yours) || String(a.due || '9').localeCompare(String(b.due || '9')));
  return rows;
}

export function TrackerFace({ rfis = [], lines = [], metrics = {}, ballOf, onOpenQuestion, onOpenLine }) {
  const rows = openRows({ rfis, lines, ballOf });
  const onYou = rows.filter((r) => r.yours).length;
  const onThem = rows.length - onYou;
  const late = rows.filter((r) => r.late).length;
  const closedLines = lines.filter((l) => CLOSED.includes(l.state)).length;
  const closed = (metrics.total_closed || 0) + closedLines;
  const tiles = [
    ['Waiting on others', onThem, onThem ? `${onThem} out with the parties` : 'nothing out', onThem > 0],
    ['Waiting on you', onYou, onYou ? `${onYou} to review or answer` : 'nothing to review or answer', onYou > 0],
    ['Overdue', late, late ? 'past the date agreed' : '—', late > 0],
    ['Closed', closed, metrics.avg_days_to_close ? `avg ${metrics.avg_days_to_close} days to close` : '—', false],
  ];
  return (
    <div className="ktr-tracker" data-testid="trail-tracker">
      <div className="ktr-tiles">
        {tiles.map(([h, c, s, attn]) => (
          <div className="ktr-tile" data-attn={attn ? '1' : undefined} key={h} data-testid={`trail-tile-${h.toLowerCase().replace(/\s+/g, '-')}`}>
            <span className="h">{h}</span><span className="c">{c}</span><span className="s">{s}</span>
          </div>
        ))}
      </div>
      <section className="kso-card" data-testid="trail-open">
        <div className="kso-card__hd">What is open <span className="n">{rows.length}</span></div>
        <div className="kso-card__bd">
          {rows.length === 0 ? (
            <p className="ktr-empty" data-testid="trail-open-empty">Nothing is open. Questions and submittals land here as they move.</p>
          ) : rows.map((r) => (
            <div className="kso-row" key={r.id} data-testid={`trail-open-${r.id}`}>
              <span className={`kso-tag ${r.tone}`}>{r.kind === 'question' ? 'Question' : 'Submittal'}</span>
              <span className="t"><b>{r.title}</b><small>{r.line}</small></span>
              {r.due ? <span className={`m${r.late ? ' late' : ''}`}>{r.late ? 'was due' : 'due'} {fmtDay(r.due)}</span> : null}
              <button type="button" className="kso-btn"
                onClick={() => (r.kind === 'question' ? onOpenQuestion(r.item) : onOpenLine(r.item))}>Open</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
